import {
  AUTH_TYPE_KEY,
  AUTH_TYPES,
  type AuthType,
  authStorage,
  MsalContextProvider,
} from '@brightdrop/auth-client';
import { BrandI18nSync } from '@brightdrop/bd-ui/brand';
import { FeatureFlagsProvider } from '@brightdrop/feature-flags-client';
import { useTranslations } from '@brightdrop/localization-client';
import { createBDTheme } from '@brightdrop/mui-theme-override';
import { TelemetryProvider } from '@brightdrop/telemetry-client';
import { datadogRum } from '@datadog/browser-rum';
import { ProfileContextProvider } from '@gm-commercial/profile-context';
import {
  Box,
  CssBaseline,
  StyledEngineProvider,
  ThemeProvider,
} from '@mui/material';
import { type ErrorInfo, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useSelector } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import FeatureFlagsContextSync from '~/common/components/featureFlagsContextSync/FeatureFlagsContextSync';
import HeapLocaleSync from '~/common/components/heapLocaleSync/HeapLocaleSync';
import Toaster from '~/common/components/toasts/Toaster';
import { msalCdiConfig, msalConfig } from '~/common/configs/auth.config';
import i18next from '~/common/configs/i18n.config';
import {
  DATADOG_LOGS_DEFAULT_CONFIG,
  DATADOG_RUM_DEFAULT_CONFIG,
} from '~/common/configs/instrumentation.config';
import useAppLocaleSync from '~/common/hooks/useAppLocaleSync';
import useClearStoredLocaleOnSignIn from '~/common/hooks/useClearStoredLocaleOnSignIn';
import { LocalStorageProvider } from '~/common/hooks/useLocalStorage';
import LocalizationGate from '~/common/localization/LocalizationGate';
import { buildLocalizationPreferences } from '~/common/localization/languageOverride/buildLocalizationPreferences';
import LanguageOverrideConsole from '~/common/localization/languageOverride/LanguageOverrideConsole';
import useLanguageOverride from '~/common/localization/languageOverride/useLanguageOverride';
import MuiDateAdapterProvider from '~/common/localization/MuiDateAdapterProvider';
import { pickerLocaleText } from '~/common/localization/pickerLocaleText';
import { SSO_ROUTES } from '~/common/models/route.model';
import {
  buildUserContext,
  PRE_AUTH_LD_CONTEXT,
  USER_CONTEXT_KIND,
} from '~/common/utils/featureFlagsUserContext';
import ScrollToTop from '~/components/utils/scrollToTop/ScrollToTop';
import {
  selectActingProfile,
  selectProfile,
  selectProfileEffectiveUserPreferences,
  selectProfileUserPreferences,
} from '~/store/profile/profileSlice.selectors';
import GlobalNavWrapper from '../features/navigationDrawer/GlobalNavWrapper';
import AppContent from './appContent/AppContent';
import ErrorFallback from './errorFallback/ErrorFallback';

const MESSAGES = {
  'common:alertMsg.messageView.pageNotFound.header': 'Page not found',
  'common:alertMsg.messageView.pageNotFound.button': 'Return home',
  'common:alertMsg.messageView.unauthorized.header': 'Unauthorized Access',
  'common:alertMsg.messageView.unauthorized.body':
    'Your account does not have access to this page.',
  'common:alertMsg.messageView.unauthorized.button': 'Go to main page',
  'common:alertMsg.messageView.serviceUnavailable.header':
    'Temporarily unavailable',
  'common:alertMsg.messageView.serviceUnavailable.body': `This page isn't available right now. Please try again later.`,

  'common:cancel': 'Cancel',
  'common:ok': 'OK',
  'common:dateRange.today': 'Today',
  'common:selectDate': 'Select date',
};

// TODO: [rebrand-internal] internal application identifier used for telemetry/logging context — rename if a broader cleanup of internal "BrightDrop Web" references is planned (not customer-visible; may impact Datadog/log filters if changed)
const APPLICATION_NAME = 'BrightDrop Web';

const logError = (error: Error, info: ErrorInfo) => {
  datadogRum.addError(error, info);
};

const getAuthProps = () => {
  const isLoginPage = SSO_ROUTES.includes(window.location.pathname);
  const params = new URLSearchParams(window.location.search);
  let authType: AuthType = AUTH_TYPES.CDI;
  if (params.has(AUTH_TYPE_KEY)) {
    authType = params.get(AUTH_TYPE_KEY) as AuthType;
  }

  if (!isLoginPage && authStorage.auth_type.getAuthType()) {
    authType = authStorage.auth_type.getAuthType() as AuthType;
  }

  const config = authType === AUTH_TYPES.SSO ? msalConfig : msalCdiConfig;

  return { authType, config };
};

const App = (): JSX.Element => {
  const { translations } = useTranslations(MESSAGES);
  const profile = useSelector(selectProfile);
  // What the user set: locale, time zone, and the rest of the legacy preference block.
  const userPreferences = useSelector(selectProfileUserPreferences);
  // What profile-api resolved: the library vocabulary that wins wherever it states a code.
  const effectiveUserPreferences = useSelector(
    selectProfileEffectiveUserPreferences
  );
  const actingProfile = useSelector(selectActingProfile);
  const { authType, config } = getAuthProps();
  const languageOverride = useLanguageOverride();

  // The shell hands over the payload and the core resolves it; the core never reads the store
  // itself, because `react-redux` is not federation-shared and each app owns its own store.
  //
  // Both blocks go over, unvalidated. `effectiveUserPreferences` is what profile-api resolved and
  // wins wherever it states a code; the legacy block and then the locale answer for the rest. The
  // core decides which of the two vocabularies a payload speaks and reports anything it cannot
  // read, so handing it a partial or unrecognised block is safe.
  const localizationPreferences = useMemo(
    () =>
      buildLocalizationPreferences(
        { userPreferences, effectiveUserPreferences },
        languageOverride
      ),
    [userPreferences, effectiveUserPreferences, languageOverride]
  );

  const ldContext = useMemo(() => {
    const userPayload = buildUserContext(profile);
    if (!userPayload) return PRE_AUTH_LD_CONTEXT;
    return { kind: 'multi' as const, [USER_CONTEXT_KIND]: userPayload };
  }, [profile]);

  useClearStoredLocaleOnSignIn();
  useAppLocaleSync(languageOverride);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={createBDTheme()}>
        <CssBaseline />
        <Router>
          <MsalContextProvider authType={authType} config={config}>
            <TelemetryProvider
              applicationName={APPLICATION_NAME}
              sessionTackingConfig={DATADOG_RUM_DEFAULT_CONFIG}
              logTrackingConfig={DATADOG_LOGS_DEFAULT_CONFIG}
              withSessionTracking={process.env.NODE_ENV === 'production'}
              withLogTracking={process.env.NODE_ENV === 'production'}
            >
              <ErrorBoundary
                FallbackComponent={ErrorFallback}
                onError={logError}
              >
                <LocalStorageProvider>
                  <Box display={'flex'} width={'100%'}>
                    <ScrollToTop />
                    <ProfileContextProvider
                      profile={profile}
                      actingProfile={actingProfile}
                    >
                      <FeatureFlagsProvider
                        clientToken={
                          globalThis.appConfig.featureFlagProviderKey
                        }
                        async
                        useLocalStorageCache
                        deferInitialization
                        context={ldContext}
                      >
                        <FeatureFlagsContextSync />
                        <LanguageOverrideConsole />
                        <HeapLocaleSync />
                        <BrandI18nSync i18n={i18next} />
                        {/* Below the flags provider, because the core cannot read the flag
                              itself. Nothing between here and where it used to sit consumes
                              localization, so the move costs nothing. */}
                        <LocalizationGate preferences={localizationPreferences}>
                          {/* The app's only date adapter, below the gate because it takes its
                                locale and its week start from the reader. */}
                          <MuiDateAdapterProvider
                            localeText={pickerLocaleText(translations)}
                          >
                            <GlobalNavWrapper>
                              <AppContent />
                              <Toaster />
                            </GlobalNavWrapper>
                          </MuiDateAdapterProvider>
                        </LocalizationGate>
                      </FeatureFlagsProvider>
                    </ProfileContextProvider>
                  </Box>
                </LocalStorageProvider>
              </ErrorBoundary>
            </TelemetryProvider>
          </MsalContextProvider>
        </Router>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default App;
